import React, { useState, useEffect, useCallback, createContext } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import { API, Auth, graphqlOperation } from "aws-amplify";
import Hidden from "@material-ui/core/Hidden";
import logger from "../logger";
import { TIPOS_CUENTA } from "../constants";
import { getStore, listStoreBySlug } from "../graphql/queries";
import Loading from "../components/Loading";
import PedidoPrint from "../components/PedidoPrint";
import { fetchProductos } from "../components/ProductosList";
import Intro from "./Intro";
import SelectorCliente from "./SelectorCliente";
import Desktop from "./app/Desktop";
import Mobile from "./app/Mobile";
import DisabledError from "./DisabledError";

const defaultConfig = {
  labelTickets: "Tickets",
  labelTicket: "Ticket",
  labelContactos: "Contactos",
  labelContacto: "Contacto",
  creaTicket: true,
  creaContacto: true
};

export const defaultStore = {
  id: null,
  body: {
    description: "",
    categories: [],
    locations: [],
    config: {},
    policies: {}
  },
  name: "",
  slug: ""
};

export const AppContext = createContext("AppContext");

export const signOut = async () => {
  await Auth.signOut();
  localStorage.clear();
};

export const fetchStore = async (storeID, onStart, onEnd, onComplete) => {
  if (storeID) {
    onStart();

    try {
      const vars = {
        id: storeID
      };

      const result = await API.graphql(graphqlOperation(getStore, vars));

      if (result.data.getStore) {
        const body = JSON.parse(result.data.getStore.body);

        const store = {
          ...result.data.getStore,
          body
        };

        onComplete(store);
      }
    } catch (err) {
      logger("App.fetchStore.error", err);
    } finally {
      onEnd();
    }
  }
};

export const checkSlugName = async slug => {
  try {
    const result = await API.graphql(
      graphqlOperation(listStoreBySlug, {
        slug
      })
    );

    if (result.data.storeBySlug.items.length > 0) return false;

    return true;
  } catch (err) {
    logger("checkSlugName.error", err);
    return false;
  }
};

const App = ({ authState, authData }) => {
  const [fetchingUsuario, setFetchingUsuario] = useState(true);
  const [usuario, setUsuario] = useState(null);
  const [introCompleted, setIntroCompleted] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState({});
  const [fetchingCuentas, setFetchingCuentas] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  const [cuenta, setCuenta] = useState(null);
  const [fetchingStore, setFetchingStore] = useState(false);
  const [store, setStore] = useState(defaultStore);
  const [fetchingProductos, setFetchingProductos] = useState(false);
  const [productosCount, setProductosCount] = useState(0);
  const [view, setView] = useState("inicio");
  const [comunas, setComunas] = useState([]);
  const [fetchingComunas, setFetchingComunas] = useState(false);

  const fetchCuentas = useCallback(async () => {
    if (cliente.id_cliente) {
      setFetchingCuentas(true);

      try {
        const init = {
          queryStringParameters: {
            cliente: cliente.id_cliente
          }
        };

        const result = await API.get(process.env.REACT_APP_HUB_API, "/cuentas", init);

        if (result.success) {
          const listCuentas = [];
          let itemCuenta = null;

          for (let i = 0; i < result.data.length; i++) {
            const item = result.data[i];
            const cliente = JSON.parse(item.cliente);
            const tipos_tickets = JSON.parse(item.tipos_tickets);
            const config_cuenta = JSON.parse(item.config_cuenta) || defaultConfig;

            const aux = {
              ...item,
              cliente,
              tipos_tickets,
              config_cuenta
            };

            if (aux.slug_cuenta === TIPOS_CUENTA.ventas) {
              itemCuenta = aux;
            }

            listCuentas.push(aux);
          }

          setCuenta(itemCuenta);
          setCuentas(listCuentas);
        }
      } catch (err) {
        logger("App.fetchCuentas.error", err);
      }

      setFetchingCuentas(false);
    }
  }, [cliente.id_cliente]);

  const fetchComunas = useCallback(async () => {
    setFetchingComunas(true);

    try {
      const result = await fetch(`${process.env.REACT_APP_URL_UTILS}/comunas`);

      if (result.status === 200) {
        const data = await result.json();
        setComunas(data);
      }
    } catch (err) {
      logger("App.fetchComunas.error", err);
    }

    setFetchingComunas(false);
  }, []);

  useEffect(() => {
    fetchComunas();
  }, [fetchComunas]);

  useEffect(() => {
    const fetchUsuario = async userId => {
      setFetchingUsuario(true);

      try {
        const init = {
          queryStringParameters: {
            userId
          }
        };
        const result = await API.get(process.env.REACT_APP_HUB_API, "/usuarios", init);

        if (result.success) {
          const data = result.data;

          if (data) {
            const config_usuario = JSON.parse(data.config_usuario);
            const clientes = JSON.parse(data.clientes);
            const user = { ...data, config_usuario, clientes };
            setUsuario(user);
          } else {
            setUsuario(null);
          }
        }
      } catch (err) {
        logger("App.fetchUsuario.error", err);
      }

      setFetchingUsuario(false);
    };

    if (authState === "signedIn" || introCompleted) {
      if (authData && !authData.challengeName) {
        fetchUsuario(authData.username);
      }
    }
  }, [authState, authData, introCompleted]);

  useEffect(() => {
    if (usuario) {
      if (usuario.clientes.length === 1) {
        setCliente(usuario.clientes[0]);
      } else if (usuario.clientes.length > 1) {
        setClientes(usuario.clientes);
      }
    }
  }, [usuario]);

  useEffect(() => {
    fetchCuentas();
    fetchStore(
      cliente.id_cliente,
      () => setFetchingStore(true),
      () => setFetchingStore(false),
      setStore
    );
  }, [cliente.id_cliente, fetchCuentas]);

  useEffect(() => {
    if (store.id) {
      fetchProductos(
        store.id,
        () => {
          setFetchingProductos(true);
        },
        () => {
          setFetchingProductos(false);
        },
        productos => {
          setProductosCount(productos.length);
        }
      );
    }

    if (store.name) {
      document.title = `${store.name} - Carrito Fácil`;
    } else {
      document.title = "Carrito Fácil";
    }
  }, [store.id, store.name]);

  if (authState !== "signedIn" || (authData || {}).challengeName) {
    return null;
  }

  const context = {
    authState,
    authData,
    usuario,
    store,
    clientes,
    cliente,
    cuentas,
    cuenta,
    view,
    productosCount,
    comunas,
    onProductosCountChange: count => setProductosCount(count),
    onClienteChange: newCliente => setCliente(newCliente),
    onCuentaChange: newCuenta => setCuenta(newCuenta),
    onStoreChange: updated => setStore(updated),
    onViewChange: view => setView(view)
  };

  const Shop = () => {
    return !Boolean(cliente.id_cliente) ? (
      <SelectorCliente />
    ) : (
      <>
        <Hidden mdUp>
          <Mobile usuario={usuario} />
        </Hidden>

        <Hidden smDown>
          <Desktop usuario={usuario} />
        </Hidden>
      </>
    );
  };

  const Pedido = ({
    match: {
      params: { id }
    }
  }) => {
    return <PedidoPrint id={id} />;
  };

  return (
    <AppContext.Provider value={context}>
      {fetchingUsuario ||
      fetchingStore ||
      fetchingCuentas ||
      fetchingProductos ||
      fetchingComunas ? (
        <Loading show />
      ) : !Boolean(usuario) ? (
        <Intro authData={authData} usuario={usuario} onClompleted={() => setIntroCompleted(true)} />
      ) : (usuario && usuario.estado_usuario < 1) || (store && store.status < 1) ? (
        <DisabledError />
      ) : (
        <Router>
          <Switch>
            <Route exact path="/" render={Shop} />
            <Route exact path="/pedido/:id" component={Pedido} />
            <Redirect to="/" />
          </Switch>
        </Router>
      )}
    </AppContext.Provider>
  );
};

export default App;
